import { formatTableDateTime } from '@/shared/utils/app-timezone';
import { todayFilterDateRange } from '@/shared/utils/date-range';
function readIncomingString(raw, ...keys) {
    for (const key of keys) {
        const value = raw[key];
        if (typeof value === 'string' && value.trim())
            return value.trim();
    }
    return '';
}
function readIncomingNumber(raw, ...keys) {
    for (const key of keys) {
        const value = raw[key];
        if (typeof value === 'number' && Number.isFinite(value))
            return value;
        if (typeof value === 'string' && value.trim()) {
            const parsed = Number(value);
            if (Number.isFinite(parsed))
                return parsed;
        }
    }
    return undefined;
}
/** Normalize `/api/qstat/incoming-calls` payloads for report UI and CSV export. */
export function normalizeIncomingCallRecord(raw) {
    const row = (raw ?? {});
    const systemDisposition = readIncomingString(row, 'systemDisposition', 'event');
    const callId = readIncomingString(row, 'callId', 'uniqueid');
    const uniqueid = readIncomingString(row, 'uniqueid', 'callId');
    return {
        callTime: readIncomingString(row, 'callTime'),
        agent: readIncomingString(row, 'agent'),
        queueName: readIncomingString(row, 'queueName'),
        phoneNumber: readIncomingString(row, 'phoneNumber'),
        duration: readIncomingString(row, 'duration'),
        callId: callId || uniqueid,
        uniqueid: uniqueid || callId,
        event: systemDisposition,
        systemDisposition,
        billsec: readIncomingNumber(row, 'billsec') ?? 0,
        cdrDuration: readIncomingNumber(row, 'cdrDuration') ?? 0,
        trunkName: readIncomingString(row, 'trunkName'),
        callType: readIncomingString(row, 'callType'),
        customerRingingTime: readIncomingNumber(row, 'customerRingingTime'),
        ivrTime: readIncomingString(row, 'ivrTime'),
        customerTalkTime: readIncomingNumber(row, 'customerTalkTime'),
        customerHoldDuration: readIncomingString(row, 'customerHoldDuration'),
        channel: readIncomingString(row, 'channel'),
        agentName: readIncomingString(row, 'agentName'),
        transferToAgent: readIncomingString(row, 'transferToAgent', 'transferToAgentName'),
        agentTalkTime: readIncomingNumber(row, 'agentTalkTime'),
        agentRingingTime: readIncomingNumber(row, 'agentRingingTime'),
        acwDuration: readIncomingString(row, 'acwDuration'),
        actualChannel: readIncomingString(row, 'actualChannel'),
        hangupDetails: readIncomingString(row, 'hangupDetails'),
        hangupTime: readIncomingString(row, 'hangupTime'),
    };
}
export function incomingCallDisposition(row) {
    return row.systemDisposition?.trim() || row.event?.trim() || '';
}
export function formatIncomingReportCell(value) {
    if (value == null)
        return '—';
    if (typeof value === 'string' && !value.trim())
        return '—';
    return String(value);
}
export function formatIncomingSeconds(value) {
    if (value == null || !Number.isFinite(value))
        return '—';
    return `${value}s`;
}
export function formatIncomingHangup(row) {
    const details = row.hangupDetails?.trim();
    const time = row.hangupTime?.trim();
    if (details && time)
        return `${details} (${time})`;
    return details || time || '—';
}
function readOutgoingString(raw, ...keys) {
    for (const key of keys) {
        const value = raw[key];
        if (typeof value === 'string' && value.trim())
            return value.trim();
    }
    return '';
}
function readOutgoingNumber(raw, ...keys) {
    for (const key of keys) {
        const value = raw[key];
        if (typeof value === 'number' && Number.isFinite(value))
            return value;
        if (typeof value === 'string' && value.trim()) {
            const parsed = Number(value);
            if (Number.isFinite(parsed))
                return parsed;
        }
    }
    return undefined;
}
/** Normalize `/api/qstat/outgoing-calls` payloads for report UI and CSV export. */
export function normalizeOutgoingCallRecord(raw) {
    const row = (raw ?? {});
    const uniqueid = readOutgoingString(row, 'uniqueid');
    const systemDisposition = readOutgoingString(row, 'systemDisposition', 'disposition');
    const disposition = readOutgoingString(row, 'disposition', 'systemDisposition');
    const src = readOutgoingString(row, 'src');
    const sipUser = readOutgoingString(row, 'sipUser') || src;
    return {
        uniqueid,
        src,
        dst: readOutgoingString(row, 'dst'),
        disposition: disposition || systemDisposition,
        duration: readOutgoingNumber(row, 'duration') ?? 0,
        billsec: readOutgoingNumber(row, 'billsec') ?? 0,
        callTime: readOutgoingString(row, 'callTime'),
        phone: readOutgoingString(row, 'phone', 'dst'),
        callType: readOutgoingString(row, 'callType'),
        systemDisposition: systemDisposition || disposition,
        hangupDetails: readOutgoingString(row, 'hangupDetails'),
        customerRingingTime: readOutgoingNumber(row, 'customerRingingTime'),
        customerTalkTime: readOutgoingNumber(row, 'customerTalkTime'),
        customerHoldDuration: readOutgoingString(row, 'customerHoldDuration'),
        actualChannel: readOutgoingString(row, 'actualChannel'),
        userId: readOutgoingNumber(row, 'userId'),
        userName: readOutgoingString(row, 'userName'),
        agentRingingTime: readOutgoingNumber(row, 'agentRingingTime'),
        agentTalkTime: readOutgoingNumber(row, 'agentTalkTime'),
        acwDuration: readOutgoingString(row, 'acwDuration'),
        campaignName: readOutgoingString(row, 'campaignName'),
        trunkName: readOutgoingString(row, 'trunkName'),
        numOfAttempts: readOutgoingNumber(row, 'numOfAttempts'),
        transferToAgent: readOutgoingString(row, 'transferToAgent', 'transferToAgentName'),
        sipUser,
    };
}
export function outgoingCallDisposition(row) {
    return row.systemDisposition?.trim() || row.disposition?.trim() || '';
}
/** Incoming call dispositions — `GET /api/qstat/incoming-calls?dispositions.in=...` */
export const INCOMING_CALL_EVENTS = [
    'ANSWERED',
    'NO_ANSWER',
    'FAILED',
    'IVR_DROPOFF'
];
export const INCOMING_CALL_EVENT_OPTIONS = INCOMING_CALL_EVENTS.map((value) => ({
    label: value,
    value,
}));
/** Outgoing CDR dispositions — `GET /api/qstat/outgoing-calls?dispositions.in=...` */
export const OUTGOING_DISPOSITIONS = [
    'ANSWERED',
    'NO ANSWER',
    'BUSY',
    'FAILED',
    'CONGESTION',
];
export const OUTGOING_DISPOSITION_OPTIONS = OUTGOING_DISPOSITIONS.map((value) => ({
    label: value,
    value,
}));
export const INCOMING_QUEUE_LANGUAGE_OPTIONS = [
    { label: 'Dari', value: 'dari' },
    { label: 'English', value: 'english' },
    { label: 'Pashto', value: 'pashto' },
];
/** Queue names grouped by language for incoming-calls `queues.in` filter. */
export const INCOMING_QUEUE_LANGUAGE_QUEUES = {
    dari: ['azb-info-dari', 'wfp-cust-dari', 'azb-cust-dari'],
    english: ['azb-cust-english', 'azb-info-English', 'wfp-cust-english'],
    pashto: ['azb-cust-pashto', 'azb-info-Pashto', 'wfp-cust-pashto'],
};
export function resolveIncomingQueuesFromLanguages(languages) {
    const names = new Set();
    for (const lang of languages) {
        for (const queue of INCOMING_QUEUE_LANGUAGE_QUEUES[lang] ?? []) {
            names.add(queue);
        }
    }
    return [...names];
}
export const QSTAT_CALL_TYPE_FILTER_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Agent Call', value: 'AGENT' },
    { label: 'Customer Call', value: 'CUSTOMER' },
];
export const QSTAT_TRUNK_NAME_FILTER_OPTIONS = [
    { label: 'All trunks', value: '' },
    { label: 'AWCC', value: 'awcc' },
    { label: 'Roshan', value: 'roshan' },
];
export const QSTAT_TRUNK_NAME_MULTISELECT_OPTIONS = QSTAT_TRUNK_NAME_FILTER_OPTIONS.filter((option) => option.value !== '');
export function resolveQstatCallTypeParam(callType) {
    return callType || undefined;
}
/** Default report window: last 7 days through today. */
export function defaultQstatReportDateRange() {
    const { startDate, endDate } = todayFilterDateRange();
    return { start: startDate, end: endDate };
}
export function formatQstatCallDate(raw) {
    return formatTableDateTime(raw);
}
export function formatSecondsToHms(seconds) {
    const total = Math.max(0, Math.floor(seconds));
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return [hrs, mins, secs].map((n) => String(n).padStart(2, '0')).join(':');
}
export function formatCompactDuration(seconds) {
    if (seconds <= 0)
        return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const parts = [];
    if (hours > 0)
        parts.push(`${hours}h`);
    if (minutes > 0)
        parts.push(`${minutes}m`);
    parts.push(`${secs}s`);
    return parts.join(' ');
}
export function formatAcwDuration(seconds) {
    if (seconds <= 0)
        return '0s';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0)
        return `${minutes}m ${secs}s`;
    return `${secs}s`;
}
export function uniqueRefOptions(values) {
    const seen = new Set();
    const options = [];
    for (const value of values) {
        const v = value?.trim();
        if (!v || seen.has(v))
            continue;
        seen.add(v);
        options.push({ label: v, value: v });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
}
export function buildAgentFilterOptions(users, extraPhoneUsers) {
    const byPhoneUser = new Map();
    for (const user of users) {
        const phoneUser = user.phoneUser?.trim();
        if (!phoneUser)
            continue;
        byPhoneUser.set(phoneUser, user.name?.trim() || phoneUser);
    }
    for (const phoneUser of extraPhoneUsers) {
        const value = phoneUser?.trim();
        if (!value || byPhoneUser.has(value))
            continue;
        byPhoneUser.set(value, value);
    }
    return [...byPhoneUser.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
